import { useMemo } from 'react';

import {
	getCloudinaryAssetUrlAutoFormat,
	isCloudinaryAsset,
} from '../../../utils/cloudinary/getCloudinaryAssetUrlAutoFormat';
import { cleanYoutubeUrl } from '../../../utils/video/cleanYoutubeUrl';
import { getYouTubeId } from '../../../utils/video/getYouTubeId';
import { isCustomVideoFormat } from '../../../utils/video/isCustomVideoFormat';
import { isYoutubeVideo } from '../../../utils/video/isYoutubeVideo';
import { VideoPlayerOptions } from './types';

interface UseVideoPlayerDetectionOptions {
	url?: string;
	cloudinaryHlsEnabled?: boolean;
}

export const useVideoPlayerDetection = ({
	url,
	cloudinaryHlsEnabled,
}: UseVideoPlayerDetectionOptions): VideoPlayerOptions | null => {
	const isYoutubeVideo_ = useMemo(() => url && isYoutubeVideo(url), [url]);

	const isCloudinaryVideo_ = useMemo(
		() => url && !isYoutubeVideo_ && isCloudinaryAsset(url),
		[isYoutubeVideo_, url],
	);

	const cloudinaryVideoUrlAutoFormat = useMemo(
		() =>
			url &&
			isCloudinaryVideo_ &&
			!isYoutubeVideo_ &&
			getCloudinaryAssetUrlAutoFormat(url, 'video', cloudinaryHlsEnabled),
		[isCloudinaryVideo_, isYoutubeVideo_, url, cloudinaryHlsEnabled],
	);

	const isCustomVideoFormat_ = useMemo(() => url && isCustomVideoFormat(url), [url]);

	if (!url) {
		return null;
	}

	if (isYoutubeVideo_) {
		const cleanedYoutubeUrl = cleanYoutubeUrl(url);
		const videoId = getYouTubeId(cleanedYoutubeUrl);
		if (!videoId) return null;
		return { player: 'youtube', videoId };
	}

	if (isCloudinaryVideo_) {
		if (cloudinaryVideoUrlAutoFormat && 'hlsSrc' in cloudinaryVideoUrlAutoFormat) {
			return { player: 'cloudinaryHLS', src: cloudinaryVideoUrlAutoFormat.hlsSrc };
		} else if (
			cloudinaryVideoUrlAutoFormat &&
			'autoFormatVideoSrc' in cloudinaryVideoUrlAutoFormat
		) {
			return { player: 'native', src: cloudinaryVideoUrlAutoFormat.autoFormatVideoSrc };
		}
	}

	if (isCustomVideoFormat_) {
		return { player: 'native', src: url };
	}

	return null;
};
